// StepAddBundle.jsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";

// MUI Components
import {
  Box, Button, Grid, Typography, IconButton, Avatar,
  List, Tooltip, Tab, CircularProgress, Backdrop, Collapse,
  TextField, MenuItem, FormControlLabel, Checkbox, FormLabel
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useTheme, styled } from "@mui/material/styles";
import { TabContext, TabPanel } from "@mui/lab";
import MuiTabList from "@mui/lab/TabList";

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Divider from "@mui/material/Divider";

// Custom Imports
import Icon from "components/icon";
import AddIcon from "@mui/icons-material/Add";
import AddEmployeeDoodle from "images/AddEmployeeDoodle.png";

import { useDropzone } from "react-dropzone";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from 'react-toastify';
import CreateBundleDialog from "screens/Services/component/CreateBundle";
import { useIndexLogic } from "screens/Services/logic/index-logic";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const TabList = styled(MuiTabList)(({ theme }) => ({
  borderBottom: "0 !important",
  width: '90%',
  display: 'flex',
  justifyContent: 'center',
  "&, & .MuiTabs-scroller": {
    padding: theme.spacing(1.25, 1.25, 2),
    margin: `${theme.spacing(-1.25, -1.25, -2)} !important`,
    backgroundColor: '#F3F4F4',
    maxWidth: 'max-content',
    borderRadius: theme.shape.borderRadius,
    justifyContent: 'center',
  },
  "& .MuiTabs-flexContainer": {
    justifyContent: 'center',
  },
  "& .MuiTabs-indicator": { display: "none" },
  "& .Mui-selected": {
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.common.white,
    color: `${theme.palette.main.main} !important`,
  },
  "& .MuiTab-root": {
    justifyContent: 'center',
    lineHeight: 1,
    minHeight: '30px', 
    fontSize: '0.8rem',
    borderRadius: theme.shape.borderRadius,
  }
}));

const StepAddBundle = ({ handlePrev, handleNext }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [alignment, setAlignment] = useState("enterManually");
  const [loading, setLoading] = useState(false);
  const [openBundles, setOpenBundles] = useState({});

  const { IndexProps } = useIndexLogic({toast});
  const { 
    openBundleDlg, 
    handleOpenBundleDlg, 
    handleCloseBundleDlg, 
    bundleContent, 
    BundleProps,
    bundleList,
    handleDeleteBundle,
    handleEditBundle,
    
  } = IndexProps;

  const columns = [
    {
      flex: 0.175,
      minWidth: 160,
      field: "serviceOfferingName",
      headerName: "Service Name",
      renderCell: (params) => {
        const { row } = params;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.serviceOfferingName}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.15,
      minWidth: 140,
      field: "flatRate",
      headerName: "Flat Rate",
      renderCell: (params) => {
        const { row } = params;

  
        const formattedRate = row.flatRate
      ? `$ ${Number(row.flatRate).toLocaleString("en-US")}`
      : "-";
      
    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Typography noWrap variant="body1">
          {formattedRate}
        </Typography>
      </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.1,
      minWidth: 140,
      sortable: false,
      renderCell: ({ row }) => {
        return(
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size="small" onClick={() => handleEditBundle(row.bundleID)}>
              <Icon icon="tabler:pencil" />
            </IconButton>
          </Box>
        )
      },
    }
  ]

  const toggleBundle = (id) => {
    setOpenBundles(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

   const showConfirmationBundleDelete = (id) => {
     MySwal.fire({
    title: '<strong>Are you sure?</strong>',
    html: '<span>You won\'t be able to revert this!</span>',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'No, keep it',
     width: '390px', // Explicit width control
  padding: '1rem',
    focusConfirm: false,
     showCloseButton: true,
     closeButtonHtml: '&times;', // This is the × symbol
    customClass: {
       popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
          htmlContainer: 'custom-swal-html',
          confirmButton: 'custom-swal-confirm',
         cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
          closeButton: 'custom-swal-close'
    }
  }).then((result) => {
      if (result.isConfirmed) {
        // Perform delete action here
        handleDeleteBundle(id);        
      }
    });
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        px: 2,
        py: 2,
        pt: { xs: 3, sm: "30px", md: "30px", lg: 2, xl: 2 },
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              width: { xs: "100%", lg: "56%" },
              mx: "auto",
              gap: { xs: 3, sm: 0 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
                textAlign: { xs: "center", sm: "left" },
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "white",
                  border: "1px solid #e0e0e0",
                  color: "text.primary",
                  height: { xs: 40, sm: 52 },
                  width: { xs: 40, sm: 52 },
                  mb: { xs: 1, sm: 0 },
                }}
              >
                <Icon icon="tabler:package" />
              </Avatar>
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontSize: { xs: "1.24rem", sm: "1.24rem" } }}
                >
                  Add Bundles
                </Typography>
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: "0.875rem", sm: "0.9rem" },
                  }}
                >
                  Create packages that contain multiple services.
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                width: { xs: "100%", sm: "auto" }, // Make container full width
                display: "flex",
                justifyContent: { xs: "center", sm: "flex-start" },
                mr: { xs: 0, sm: 10 },
                mt: { xs: 2, sm: 0 },
              }}
            >
              <Button
                variant="contained"
                color="main"
                startIcon={<AddIcon />}
                onClick={handleOpenBundleDlg}
                sx={{
                  px: 3,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Create a Bundle
              </Button>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TabContext value={alignment}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              {/* <TabList onChange={(e, val) => setAlignment(val)}>
                <Tab value="enterManually" label="Add Bundles" />
                <Tab value="uploadFile" label="Import Bundles" />
              </TabList> */}
            </Box>
            <TabPanel
              value="enterManually"
              sx={{ maxWidth: { xs: "100%", lg: "60%" }, mx: "auto" }}
            >
              {bundleList.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mx: "auto",
                    border: "1px solid #EAEAEA",
                    borderRadius: 1,
                    maxWidth: "100%",
                    py: 4,
                    height: 220,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={AddEmployeeDoodle}
                      alt="Empty State"
                      style={{ width: "100px", height: "auto" }}
                    />
                    <Typography variant="h6">No Bundle added yet</Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {bundleList.map((bundle) => (
                    <Box
                      key={bundle.id}
                      sx={{
                        border: "1px solid #E1E4EA",
                        borderRadius: 1,
                        width: { xs: "100%", lg: "95%" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 2,
                          cursor: "pointer",
                          backgroundColor: "transparent",
                        }}
                        onClick={() => toggleBundle(bundle.id)}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Icon
                            icon={
                              openBundles[bundle.id]
                                ? "tabler:chevron-down"
                                : "tabler:chevron-right"
                            }
                          />
                          <Typography
                            variant="body1"
                            sx={{
                              ml: { xs: 2, sm: 2 },
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            {bundle.bundleName}
                          </Typography>
                          <Box
                            sx={{
                              height: 20,
                              minWidth: 50,
                              padding: "0 6px",
                              fontSize: "0.75rem",
                              display: "flex",
                              alignItems: "center",
                              borderRadius: "4px",
                              backgroundColor: "#23A26D",
                              color: "#fff",
                              ml: { xs: 1, sm: 2 },
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="white"
                              sx={{
                                fontSize: "0.65rem",
                                lineHeight: 1,
                                textAlign: "center",
                              }}
                            >
                              BUNDLE
                            </Typography>
                          </Box>
                        </Box>

                        <IconButton
                          color="error"
                          onClick={() =>
                            showConfirmationBundleDelete(bundle.id)
                          }
                        >
                          <Icon icon="tabler:trash" />
                        </IconButton>
                      </Box>
                      <Divider sx={{ borderColor: "#E1E4EA" }} />
                      <Collapse
                        in={openBundles[bundle.id]}
                        timeout="auto"
                        unmountOnExit
                        sx={{ backgroundColor: "transparent" }}
                      >
                        <Box sx={{ p: 4 }}>
                          <Grid container spacing={4}>
                            <Grid item xs={12}>
                              {/* ID and Description Row */}
                              <Box sx={{ display: "flex", gap: 26, mb: 4 }}>
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500, mb: 1 }}
                                  >
                                    ID
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 400 }}
                                  >
                                    {bundle.id}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500, mb: 1, ml: 2.2 }}
                                  >
                                    Description
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 400, ml: 2.2 }}
                                  >
                                    {bundle.description}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Metadata Fields */}
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 18,
                                  flexWrap: "wrap",
                                  mb: 1,
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      color: "text.secondary",
                                      mb: 0.5,
                                    }}
                                  >
                                    Flat Rate
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 400 }}
                                  >
                                    $
                                    {Number(
                                      bundle.totalFlatRate
                                    ).toLocaleString("en-US")}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      color: "text.secondary",
                                      mb: 0.5,
                                    }}
                                  >
                                    Status
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      px: 1.5,
                                      py: 0.5,
                                      backgroundColor: "#E9FBF0",
                                      borderRadius: 2,
                                      fontWeight: 500,
                                      fontSize: "0.75rem",
                                      color: "#4CAF50",
                                      width: "fit-content",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        backgroundColor: "#4CAF50",
                                        mr: 1,
                                      }}
                                    />
                                    {bundle?.isActive ? "Active" : "Inactive"}
                                  </Box>
                                </Box>
                              </Box>
                              {bundle?.services?.length > 0 && (
                                <Box sx={{ height: 170, width: "100%", mt: 4 }}>
                                  <DataGridPro
                                    rows={bundle?.services.map(
                                      (service, index) => ({
                                        ...service,
                                        id: index,
                                        bundleID: bundle.id,
                                      })
                                    )}
                                    columns={columns}
                                    disableColumnMenu
                                    hideFooter
                                    headerHeight={40}
                                    rowHeight={40}
                                    sx={{
                                      "& .MuiDataGrid-columnHeaders": {
                                        textTransform: "none", // ✅ disables uppercase
                                      },
                                      "& .MuiDataGrid-columnHeaderTitle": {
                                        fontWeight: 500,
                                        fontSize: "0.875rem",
                                        textTransform: "none", // ✅ ensures header text is styled as written
                                      },
                                    }}
                                  />
                                </Box>
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </Box>
                  ))}
                </Box>
              )}
            </TabPanel>
          </TabContext>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: { xs: "100%", lg: "55%" },
          pt: 2,
          pr: { xs: 0, lg: 8 },
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          sx={{
            textTransform: "none",
            borderColor: "transparent",
            color: "text.secondary",
            "&:hover": { borderColor: "transparent", color: "text.secondary" },
          }}
          onClick={handlePrev}
        >
          Previous
        </Button>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="text"
            color="secondary"
            size="small"
            onClick={() => handleNext([])}
          >
            Skip for Now
          </Button>
          <Button
            variant="contained"
            color="main"
            onClick={() => handleNext([])}
          >
            Next
          </Button>
        </Box>
      </Box>

      <CreateBundleDialog
        open={openBundleDlg}
        handleClose={handleCloseBundleDlg}
        bundleContent={bundleContent}
        BundleProps={BundleProps}
      />
      {/*<ToastContainer />*/}

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default StepAddBundle;